import './customer-refresh.css';
/**
 * Customer portal routes - owner: ui-shell. Edit only inside this folder.
 * One folder per screen; register it in this array. Paths here are what notification deep links use.
 */
import type { RouteObject } from 'react-router';
import { HomePage } from './home/HomePage';
import { ApprovalDetailPage, ApprovalsPage } from './approvals/ApprovalsPage';
import { AuditPage, AuditRedirect } from './audit/AuditPage';
import { AuditDetailPage } from './audit/AuditDetailPage';
import { HelpPage } from './help/HelpPage';
import { OpportunitiesPage, OpportunityDetailPage } from './opportunities/OpportunitiesPage';
import { ModuleGate } from './_shared/ModuleGate';
import { GATED_ROUTES } from '../../shell/nav';
import { AuditJobPage } from './audit/AuditJobPage';
import { GrowthAuditDashboard } from './audit/GrowthAuditDashboard';
import { AdvancedReportPage } from './audit/advanced/report';
import { LeadsPage } from './leads/LeadsPage';
import { InboxPage } from './inbox/InboxPage';
import { CalendarPage } from './calendar/CalendarPage';
import { AdsPage } from './ads/AdsPage';
import { AiActivityPage } from './ai-activity/AiActivityPage';
import { CommandCenterPage } from './command-center/CommandCenterPage';
import { PerformancePage } from './performance/PerformancePage';
import { AnalyticsPage } from './analytics/AnalyticsPage';
import { ContentPage } from './content/ContentPage';
import { AssetsPage } from './content/AssetsPage';
import { SetupPage } from './setup/SetupPage';
import { TeamPage } from './team/TeamPage';
import { InviteAcceptPage } from './team/InviteAcceptPage';
import { BillingPage } from './billing/BillingPage';
import { NotificationsPage } from './notifications/NotificationsPage';
import { SettingsPage } from './settings/SettingsPage';
import { SearchPage } from './search/SearchPage';
import { SignInPage } from './signin/SignInPage';
import { SignUpPage } from './signup/SignUpPage';
import { IntegrationsPage } from './integrations/IntegrationsPage';
import { ConnectCallbackPage } from './integrations/ConnectCallbackPage';
import { WhatsAppConnectCallbackPage } from './whatsapp/WhatsAppConnectCallbackPage';
import { WhatsAppConnectPage } from './whatsapp/WhatsAppConnectPage';
import { SecurityPage } from './security/SecurityPage';
import { KnowledgePage } from './knowledge/KnowledgePage';
import { AutoRagPage } from '../../features/auto-rag/AutoRagPage';
import { BrandingPage } from './branding/BrandingPage';
import { ModulePage } from './modules/ModulePage';
import { ViewAsPage } from './view-as/ViewAsPage';
import { SeoApprovalPage } from './seo-approval/SeoApprovalPage';
import { ApprovalsInboxPage } from './audit/advanced/actions';
import { AdvancedIntegrationsPage } from './audit/advanced/integrations/IntegrationsPage';
import { MonitoringPage } from './audit/advanced/monitoring/MonitoringPage';
import { SecurityPage as AdvancedSecurityPage } from './audit/advanced/security/SecurityPage';

export const CUSTOMER_PATHS = [
  '/', '/approvals', '/approvals/:id', '/audit', '/audit/dashboard', '/audit/new', '/audit/:id', '/leads', '/inbox', '/calendar', '/ads', '/performance', '/analytics', '/command-center', '/ai-activity', '/content', '/content/assets',
  '/setup', '/knowledge', '/knowledge/ai-search', '/team', '/billing', '/notifications', '/settings', '/settings/security', '/integrations', '/integrations/connect', '/whatsapp/connect', '/whatsapp/connect/callback', '/search', '/signin', '/signup',
  '/sign-in', '/sign-in/link', '/invite', '/join', '/accept-invite', '/view-as',
  // Quixo OS menu (8 Oct 2026): module pages + white label (branding)
  '/visibility', '/crm', '/campaigns', '/deliverables', '/reports', '/organization', '/help', '/branding',
  // Advanced audit remediation (T5): approvals inbox, integrations, monitoring, security
  '/audit/advanced/actions', '/audit/advanced/integrations', '/audit/advanced/monitoring', '/audit/advanced/security',
  // Advanced audit report (T4)
  '/audit/:id/advanced',
  // Opportunities: requirements, quotation and negotiation with the account manager (flag crm.opportunities)
  '/opportunities', '/opportunities/:id',
] as const;

/**
 * Standalone pages (no shell): mounted under AuthLayout by routes/index.tsx. /sign-in/link is where the
 * API's magic-link emails point (`https://<host>/sign-in/link#token=`); /sign-in is the host-neutral alias.
 * /view-as is the Super Admin "view as" landing (`#code=`): public, it creates the session.
 */
export const CUSTOMER_AUTH_PATHS = ['/signin', '/signup', '/sign-in', '/sign-in/link', '/view-as'] as const;
export const customerAuthRoutes: RouteObject[] = [
  { path: 'signin', element: <SignInPage /> },
  { path: 'sign-in', element: <SignInPage /> },
  { path: 'sign-in/link', element: <SignInPage /> },
  { path: 'signup', element: <SignUpPage /> },
  { path: 'view-as', element: <ViewAsPage /> },
];

/**
 * Public token pages with their own minimal frame (no shell, no AuthLayout hero, no session): mounted by routes/index.tsx.
 * /a/:token is the SEO loop client approval link (flags seo-loop.enabled + seo-loop.approval_links, enforced by the API).
 */
export const CUSTOMER_PUBLIC_PATHS = ['/a/:token'] as const;
export const customerPublicRoutes: RouteObject[] = [
  { path: 'a/:token', element: <SeoApprovalPage /> },
];

const routes: RouteObject[] = [
  { index: true, element: <HomePage /> },
  { path: 'approvals', element: <ApprovalsPage /> },
  { path: 'approvals/:id', element: <ApprovalDetailPage /> },
  { path: 'audit', element: <AuditPage /> },
  { path: 'audit/dashboard', element: <GrowthAuditDashboard /> },
  { path: 'audit/new', element: <AuditJobPage /> },
  { path: 'audit/:id', element: <AuditDetailPage /> },
  { path: 'audit/:id/advanced', element: <AdvancedReportPage /> },
  { path: 'audit/advanced/actions', element: <ApprovalsInboxPage /> },
  { path: 'audit/advanced/integrations', element: <AdvancedIntegrationsPage /> },
  { path: 'audit/advanced/monitoring', element: <MonitoringPage /> },
  { path: 'audit/advanced/security', element: <AdvancedSecurityPage /> },
  // old links: report-ready emails (/audits/:id) and the session status page (/audit/status/:id)
  { path: 'audits/:id', element: <AuditRedirect /> },
  { path: 'audit/status/:id', element: <AuditRedirect /> },
  { path: 'leads', element: <LeadsPage /> },
  { path: 'inbox', element: <InboxPage /> },
  { path: 'calendar', element: <CalendarPage /> },
  { path: 'ads', element: <AdsPage /> },
  { path: 'performance', element: <PerformancePage /> },
  // Google Analytics 4 (flag connectors.ga4; the page 404s and the nav entry hides when off)
  { path: 'analytics', element: <AnalyticsPage /> },
  // Growth OS phase 2 (flag growth_os.command_center; the page 404s and the nav entry hides when off)
  { path: 'command-center', element: <CommandCenterPage /> },
  // AI activity trail (flag ai.activity_ui; the page 404s and the nav entry hides when off)
  { path: 'ai-activity', element: <AiActivityPage /> },
  { path: 'content', element: <ContentPage /> },
  // asset library + import from Google Drive / OneDrive (assets.cloud_import)
  { path: 'content/assets', element: <AssetsPage /> },
  { path: 'setup', element: <SetupPage /> },
  { path: 'knowledge', element: <KnowledgePage /> },
  { path: 'knowledge/ai-search', element: <AutoRagPage /> },
  { path: 'team', element: <TeamPage /> },
  { path: 'billing', element: <BillingPage /> },
  { path: 'notifications', element: <NotificationsPage /> },
  { path: 'settings', element: <SettingsPage /> },
  { path: 'settings/security', element: <SecurityPage /> },
  { path: 'integrations', element: <IntegrationsPage /> },
  // connectors OAuth return: the worker's /bff/oauth/connectors/callback 303s here with the result in the fragment
  { path: 'integrations/connect', element: <ConnectCallbackPage /> },
  // full document load only (worker relaxes CSP/COOP for this path): link to it with <a href>, never <Link>
  { path: 'whatsapp/connect', element: <WhatsAppConnectPage /> },
  // same-tab (phone) signup return: the worker 303s Meta's ?state&code here as a fragment; strict CSP (nothing from Meta)
  { path: 'whatsapp/connect/callback', element: <WhatsAppConnectCallbackPage /> },
  { path: 'search', element: <SearchPage /> },
  // QM-A12-001 invite acceptance: canonical /invite#token= and the legacy link forms
  { path: 'invite', element: <InviteAcceptPage /> },
  { path: 'join', element: <InviteAcceptPage /> },
  { path: 'accept-invite', element: <InviteAcceptPage /> },
  // Quixo OS menu: module landing pages until each dedicated workspace ships; white label = the tenant's brand kit
  { path: 'visibility', element: <ModulePage path="/visibility" /> },
  { path: 'crm', element: <ModulePage path="/crm" /> },
  { path: 'campaigns', element: <ModulePage path="/campaigns" /> },
  { path: 'deliverables', element: <ModulePage path="/deliverables" /> },
  { path: 'reports', element: <ModulePage path="/reports" /> },
  { path: 'organization', element: <ModulePage path="/organization" /> },
  { path: 'help', element: <HelpPage /> },
  { path: 'opportunities', element: <OpportunitiesPage /> },
  { path: 'opportunities/:id', element: <OpportunityDetailPage /> },
  { path: 'branding', element: <BrandingPage /> },
];

/** Phase 1 (G10): modules outside the Growth Audit redirect to the dashboard unless their flag is on. */
export const customerRoutes: RouteObject[] = routes.map((r) => {
  const path = r.path ? `/${r.path}` : null;
  const gate = path ? Object.keys(GATED_ROUTES).find((p) => path === p || path.startsWith(`${p}/`)) : undefined;
  return gate ? { ...r, element: <ModuleGate path={gate}>{r.element}</ModuleGate> } : r;
});
